/** 收款计划默认只展示“已收 + 日期”；核销方式、经办人与备注通过悬停/聚焦或手机点击查看。
 * 使用现有 Radix 浮层管理关闭与键盘交互，只展示已保存的资料，不触发收款、开票或通知。 */
import React, { useEffect, useId, useRef, useState } from "react";
import { Popover, PopoverAnchor, PopoverContent } from "../ui/popover";
import { fmtD } from "../../lib/date";

const METHOD_LABELS = {
  customer_proof: ["客户凭证核销", "Customer proof confirmed"],
  check: ["支票", "Check"], "check 支票": ["支票", "Check"],
  wire: ["电汇", "Wire"], "wire 电汇": ["电汇", "Wire"], ach: ["ACH 转账", "ACH transfer"],
  zelle: ["Zelle", "Zelle"], cash: ["现金", "Cash"], "cash 现金": ["现金", "Cash"],
  credit_card: ["信用卡", "Credit card"], "credit card 信用卡": ["信用卡", "Credit card"],
  other: ["其他", "Other"], "other 其他": ["其他", "Other"],
};
const methodLabel = (method, zh) => METHOD_LABELS[String(method || "").trim().toLowerCase()]?.[zh ? 0 : 1] || method;
const money = amount => "$" + Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ReceiptStatusDetails({ payment, zh = true, className = "" }) {
  const [open, setOpen] = useState(false);
  const trigger = useRef(null), content = useRef(null), timer = useRef(null), pinned = useRef(false);
  const id = useId();
  const pay = payment || {};
  const status = `${zh ? "已收" : "Paid"}${pay.paidAt ? ` ${String(pay.paidAt).slice(5, 10)}` : ""}`;
  const fields = [
    pay.paidAt && [zh ? "收款日期" : "Received on", fmtD(pay.paidAt)],
    pay.method && [zh ? "收款方式" : "Method", methodLabel(pay.method, zh)],
    pay.reference && [zh ? "支票号 / 流水号" : "Check / Reference", `#${pay.reference}`],
    pay.paidAmount != null && Number(pay.paidAmount) !== Number(pay.amount) && [zh ? "实收金额" : "Received amount", money(pay.paidAmount)],
    pay.recordedBy && [zh ? "经办人" : "Recorded by", pay.recordedBy],
    pay.payerName && [zh ? "付款方" : "Payer", pay.payerName],
    pay.payNote && [zh ? "备注" : "Note", pay.payNote],
  ].filter(Boolean);
  const clearClose = () => { if (timer.current) clearTimeout(timer.current); };
  useEffect(() => () => clearClose(), []);
  const changeOpen = value => { clearClose(); if (!value) pinned.current = false; setOpen(value); };
  const show = () => { clearClose(); setOpen(true); };
  const scheduleClose = () => {
    clearClose();
    timer.current = setTimeout(() => {
      const focused = document.activeElement;
      if (!pinned.current && focused !== trigger.current && !content.current?.contains(focused)) setOpen(false);
    }, 140);
  };
  // 日期已直接显示；没有其他收款资料时保持普通状态标签，避免空浮层。
  const hasDetails = fields.length > (pay.paidAt ? 1 : 0);
  if (!hasDetails) return <span className={className}>{status}</span>;
  return <Popover open={open} onOpenChange={changeOpen}>
    <PopoverAnchor asChild>
      <button ref={trigger} type="button" className={`${className} cursor-help outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2`}
        aria-label={`${status}，${zh ? "查看收款详情" : "View receipt details"}`} aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined}
        data-testid={`pay-record-trigger-${pay.stage}`}
        onPointerEnter={event => { if (event.pointerType !== "touch") show(); }} onPointerLeave={scheduleClose}
        onFocus={show} onBlur={scheduleClose} onClick={() => { pinned.current = true; show(); }}>
        {status}
      </button>
    </PopoverAnchor>
    <PopoverContent ref={content} id={id} side="bottom" align="end" collisionPadding={12}
      className="z-[150] max-h-[min(70dvh,400px)] w-72 max-w-[calc(100vw-24px)] overflow-auto p-3 text-left text-xs"
      aria-label={zh ? "收款详情" : "Receipt details"} onOpenAutoFocus={event => event.preventDefault()}
      onCloseAutoFocus={event => event.preventDefault()} onPointerEnter={clearClose} onPointerLeave={scheduleClose} onBlur={scheduleClose}>
      <p className="mb-2 text-sm font-semibold">{zh ? "收款详情" : "Receipt details"}</p>
      <dl className="space-y-2" data-testid={`pay-record-info-${pay.stage}`}>
        {fields.map(([label, value]) => <div key={label} className="grid grid-cols-[auto_minmax(0,1fr)] gap-3"><dt className="text-muted-foreground">{label}</dt><dd className="min-w-0 whitespace-pre-wrap break-words text-right [overflow-wrap:anywhere]">{value}</dd></div>)}
      </dl>
    </PopoverContent>
  </Popover>;
}
